import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { storageService } from '../../../services/storageService';
import Icon from '../../../components/AppIcon';

const InstituteProfileTab = () => {
  const { user, userProfile } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [form, setForm] = useState({
    institute_name: '',
    contact_person: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    website: '',
    description: '',
    state: '',
    logo_url: ''
  });

  // Counsellor emails state
  const [counsellorEmails, setCounsellorEmails] = useState(['']);
  const [newEmailInput, setNewEmailInput] = useState('');

  const [logoPreview, setLogoPreview] = useState('');
  const [instituteId, setInstituteId] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadProfile();
    }
  }, [user?.id, userProfile]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profile = userProfile?.institute_profiles?.[0];

      let resolvedProfile = profile;

      // Fallback: fetch directly by user_id if context doesn't have it yet
      if (!resolvedProfile && user?.id) {
        const { data: fallbackData } = await supabase
          ?.from('institute_profiles')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.single();
        if (fallbackData) resolvedProfile = fallbackData;
      }

      if (!resolvedProfile) {
        setLoading(false);
        return;
      }

      setInstituteId(resolvedProfile?.id);
      setUserId(user?.id);

      // Fetch full institute profile
      const { data: ipData, error: ipError } = await supabase?.from('institute_profiles')?.select('*')?.eq('id', resolvedProfile?.id)?.single();

      if (ipError) throw ipError;

      // Fetch user data for email and phone
      const { data: userData, error: userError } = await supabase?.from('users')?.select('email, phone, name')?.eq('id', user?.id)?.single();

      if (userError) throw userError;

      setForm({
        institute_name: ipData?.institute_name || '',
        contact_person: ipData?.contact_person || '',
        email: userData?.email || '',
        phone: userData?.phone || '',
        whatsapp_number: ipData?.whatsapp_number || userData?.phone || '',
        website: ipData?.website || '',
        description: ipData?.description || '',
        state: ipData?.state || '',
        logo_url: ipData?.logo_url || ''
      });

      setLogoPreview(ipData?.logo_url || '');

      // Load counsellor emails
      const emails = ipData?.counsellor_emails || [];
      setCounsellorEmails(emails?.length > 0 ? emails : ['']);
    } catch (err) {
      console.error('Error loading profile:', err);
      setErrorMsg('Failed to load profile data.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Counsellor email handlers
  const handleCounsellorEmailChange = (index, value) => {
    setCounsellorEmails(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAddCounsellorEmail = () => {
    setCounsellorEmails(prev => [...prev, '']);
  };

  const handleRemoveCounsellorEmail = (index) => {
    setCounsellorEmails(prev => {
      if (prev?.length === 1) return [''];
      return prev?.filter((_, i) => i !== index);
    });
  };

  const handleLogoClick = () => {
    fileInputRef?.current?.click();
  };

  const handleLogoChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setErrorMsg('Please select a valid image file.');
      return;
    }

    // Validate file size (max 2MB)
    if (file?.size > 2 * 1024 * 1024) {
      setErrorMsg('Logo image must be under 2MB.');
      return;
    }

    try {
      setUploadingLogo(true);
      setErrorMsg('');

      // Show local preview immediately
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev?.target?.result);
      reader?.readAsDataURL(file);

      // Upload to storage
      const { publicUrl } = await storageService?.uploadBoothImage(file, user?.id, 'logo');
      setForm(prev => ({ ...prev, logo_url: publicUrl }));
      setLogoPreview(publicUrl);
    } catch (err) {
      console.error('Logo upload error:', err);
      setErrorMsg('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!instituteId || !userId) return;

    // Validate counsellor emails
    const validEmails = counsellorEmails?.filter(e => e && e?.trim() !== '');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validEmails?.filter(e => !emailRegex?.test(e?.trim()));
    if (invalidEmails?.length > 0) {
      setErrorMsg(`Invalid email address(es): ${invalidEmails?.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      setErrorMsg('');
      setSuccessMsg('');

      // Update institute_profiles
      const { error: ipError } = await supabase?.from('institute_profiles')?.update({
          institute_name: form?.institute_name?.trim(),
          contact_person: form?.contact_person?.trim(),
          website: form?.website?.trim(),
          description: form?.description?.trim(),
          state: form?.state?.trim(),
          logo_url: form?.logo_url,
          counsellor_emails: validEmails,
          whatsapp_number: form?.whatsapp_number?.trim() || null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', instituteId);

      if (ipError) throw ipError;

      // Update users table for phone
      const { error: userError } = await supabase?.from('users')?.update({
          phone: form?.phone?.trim(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId);

      if (userError) throw userError;

      setSuccessMsg('Profile updated successfully!');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Save error:', err);
      setErrorMsg('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">Institute Profile</h2>
            <p className="text-sm text-muted-foreground">Update your institute's public information and contact details</p>
          </div>
        </div>
      </div>
      {/* Success / Error Messages */}
      {successMsg && (
        <div className="flex items-center gap-2 bg-success/10 border border-success/30 text-success rounded-lg px-4 py-3">
          <Icon name="CheckCircle" size={18} />
          <span className="text-sm font-medium">{successMsg}</span>
        </div>
      )}
      {errorMsg && (
        <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/30 text-destructive rounded-lg px-4 py-3">
          <Icon name="AlertCircle" size={18} />
          <span className="text-sm font-medium">{errorMsg}</span>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo Section */}
        <div className="bg-card border border-border rounded-lg p-6 flex flex-col items-center gap-4">
          <h3 className="text-base font-semibold text-foreground self-start">Institute Logo</h3>
          <div
            className="relative w-32 h-32 rounded-xl border-2 border-dashed border-border bg-muted/30 flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all overflow-hidden group"
            onClick={handleLogoClick}
          >
            {logoPreview ? (
              <>
                <img
                  src={logoPreview}
                  alt="Institute logo preview"
                  className="w-full h-full object-contain p-2"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Icon name="Camera" size={24} className="text-white" />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                {uploadingLogo ? (
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Icon name="Upload" size={28} />
                    <span className="text-xs text-center">Upload Logo</span>
                  </>
                )}
              </div>
            )}
            {uploadingLogo && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoChange}
          />
          <button
            type="button"
            onClick={handleLogoClick}
            disabled={uploadingLogo}
            className="text-sm text-primary hover:underline disabled:opacity-50"
          >
            {uploadingLogo ? 'Uploading...' : 'Change Logo'}
          </button>
          <p className="text-xs text-muted-foreground text-center">PNG, JPG or GIF. Max 2MB.</p>
        </div>

        {/* Form Fields */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6 space-y-5">
          <h3 className="text-base font-semibold text-foreground">Basic Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Institute Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Institute Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                name="institute_name"
                value={form?.institute_name}
                onChange={handleChange}
                placeholder="Enter institute name"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            {/* Contact Person */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Contact Person</label>
              <input
                type="text"
                name="contact_person"
                value={form?.contact_person}
                onChange={handleChange}
                placeholder="Name of contact person"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            {/* Email (read-only) */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Email Address</label>
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={form?.email}
                  readOnly
                  className="w-full px-3 py-2 pr-9 rounded-lg border border-border bg-muted/40 text-muted-foreground text-sm cursor-not-allowed"
                />
                <Icon name="Lock" size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground">Email cannot be changed here. Contact admin.</p>
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={form?.phone}
                onChange={handleChange}
                placeholder="+91 XXXXX XXXXX"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            {/* WhatsApp */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <span className="text-green-600">
                  <Icon name="MessageCircle" size={14} />
                </span>
                WhatsApp Number
              </label>
              <input
                type="tel"
                name="whatsapp_number"
                value={form?.whatsapp_number}
                onChange={handleChange}
                placeholder="+91 XXXXX XXXXX"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            {/* Website */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Website URL</label>
              <input
                type="url"
                name="website"
                value={form?.website}
                onChange={handleChange}
                placeholder="https://www.yourinstitute.edu"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            {/* State */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-sm font-medium text-foreground">State / Location</label>
              <input
                type="text"
                name="state"
                value={form?.state}
                onChange={handleChange}
                placeholder="e.g. Maharashtra, Delhi, Karnataka"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-sm font-medium text-foreground">Institute Description</label>
              <textarea
                name="description"
                value={form?.description}
                onChange={handleChange}
                rows={4}
                placeholder="Brief description about your institute, programs, and highlights..."
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
              />
              <p className="text-xs text-muted-foreground">{form?.description?.length || 0} characters</p>
            </div>
          </div>
        </div>
      </div>
      {/* Counsellor Escalation Emails */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon name="Bell" size={18} className="text-amber-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Counsellor Escalation Emails</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              When a student chat is escalated by the AI, all listed counsellors receive an instant email alert. Add multiple emails so if one counsellor is unavailable, others can respond.
            </p>
          </div>
        </div>

        <div className="space-y-2.5">
          {counsellorEmails?.map((email, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex-1 relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <Icon name="Mail" size={15} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleCounsellorEmailChange(index, e?.target?.value)}
                  placeholder={`Counsellor ${index + 1} email address`}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveCounsellorEmail(index)}
                disabled={counsellorEmails?.length === 1 && email === ''}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Remove email"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={handleAddCounsellorEmail}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
        >
          <Icon name="Plus" size={16} />
          Add another counsellor email
        </button>

        {counsellorEmails?.filter(e => e && e?.trim() !== '')?.length > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <Icon name="Info" size={15} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              <span className="font-semibold">{counsellorEmails?.filter(e => e && e?.trim() !== '')?.length} counsellor(s)</span> will receive escalation alerts. Emails are sent simultaneously — the first available counsellor can respond.
            </p>
          </div>
        )}
      </div>
      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || uploadingLogo}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Icon name="Save" size={16} />
              Save Profile
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default InstituteProfileTab;
